import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  fetchAdminMe, fetchInvestments, addInvestment, deleteInvestment,
  exportInvestmentsCsv, addInvestmentPhase, toggleInvestmentPhase, deleteInvestmentPhase,
} from "@/lib/api";
import {
  Wallet, Loader2, Plus, Trash2, FileDown, TrendingUp, Coins,
  CheckCircle2, Circle, ArrowLeft, PiggyBank, Target,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const fcfa = (n) => `${(n ?? 0).toLocaleString("fr-FR")} FCFA`;
const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  const names = ["Jan", "Fev", "Mar", "Avr", "Mai", "Juin", "Juil", "Aout", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(mo, 10) - 1]} ${y}`;
};

export default function AdminInvestmentsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showPhase, setShowPhase] = useState(false);
  const [form, setForm] = useState({ amount: "", credits: "", date: new Date().toISOString().slice(0, 10), description: "" });
  const [phaseForm, setPhaseForm] = useState({ phase_name: "", estimated_points: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    fetchInvestments().then(setData).catch(() => toast.error("Erreur de chargement")).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchAdminMe().then(load).catch(() => navigate("/", { replace: true }));
  }, [navigate, load]);

  const saveInvestment = async () => {
    if (!form.amount || !form.date) return toast.error("Montant et date obligatoires");
    setSaving(true);
    try {
      await addInvestment({
        amount: parseFloat(form.amount),
        credits: parseFloat(form.credits || 0),
        date: form.date,
        description: form.description,
      });
      toast.success("Investissement ajoute");
      setShowAdd(false);
      setForm({ amount: "", credits: "", date: new Date().toISOString().slice(0, 10), description: "" });
      load();
    } catch { toast.error("Erreur lors de l'ajout"); }
    setSaving(false);
  };

  const removeInvestment = async (id) => {
    if (!window.confirm("Supprimer cet investissement ?")) return;
    try { await deleteInvestment(id); toast.success("Supprime"); load(); }
    catch { toast.error("Erreur"); }
  };

  const savePhase = async () => {
    if (!phaseForm.phase_name) return toast.error("Nom de phase obligatoire");
    setSaving(true);
    try {
      await addInvestmentPhase({ phase_name: phaseForm.phase_name, estimated_points: parseFloat(phaseForm.estimated_points || 0) });
      toast.success("Phase ajoutee");
      setShowPhase(false);
      setPhaseForm({ phase_name: "", estimated_points: "" });
      load();
    } catch { toast.error("Erreur"); }
    setSaving(false);
  };

  const togglePhase = async (p) => {
    try { await toggleInvestmentPhase(p.id, !p.completed); load(); }
    catch { toast.error("Erreur"); }
  };

  const removePhase = async (id) => {
    if (!window.confirm("Supprimer cette phase ?")) return;
    try { await deleteInvestmentPhase(id); load(); }
    catch { toast.error("Erreur"); }
  };

  const downloadCsv = async () => {
    try {
      const blob = await exportInvestmentsCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `investissements_fodindin_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erreur export"); }
  };

  if (loading || !data) {
    return (
      <main className="max-w-7xl mx-auto px-6 py-20 text-center">
        <Loader2 className="animate-spin mx-auto text-orange-500" />
        <p className="mt-3 text-[var(--ink-soft)]">Chargement des investissements...</p>
      </main>
    );
  }

  const maxMonth = Math.max(...data.monthly_summary.map((m) => m.total), 1);
  const phasesPct = data.phases.length > 0 ? Math.round((data.phases_completed / data.phases.length) * 100) : 0;

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-investments-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-orange-600 font-semibold mb-4" data-testid="back-to-admin">
        <ArrowLeft size={14} /> Tableau de bord
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
            <PiggyBank size={32} className="text-orange-500" /> Investissements du projet
          </h1>
          <p className="text-[var(--ink-soft)] mt-2">Bilan mensuel des credits Emergent achetes pour construire Fodindin, brique par brique.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadCsv} data-testid="export-csv-btn">
            <FileDown size={16} className="mr-1" /> Exporter CSV
          </Button>
          <Button onClick={() => setShowAdd(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="add-investment-btn">
            <Plus size={16} className="mr-1" /> Ajouter un investissement
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="investment-summary-cards">
        <SummaryCard icon={Wallet} label="Total investi" value={fcfa(data.total_invested)} sub="depuis le debut du projet" color="text-orange-600 bg-orange-50" testid="total-invested" />
        <SummaryCard icon={Coins} label="Credits achetes" value={data.total_credits.toLocaleString("fr-FR")} sub="credits Emergent cumules" color="text-emerald-600 bg-emerald-50" testid="total-credits" />
        <SummaryCard icon={Target} label="Points utilises" value={data.used_points.toLocaleString("fr-FR")} sub={`${data.phases_completed}/${data.phases.length} phases terminees`} color="text-sky-600 bg-sky-50" testid="used-points" />
        <SummaryCard icon={TrendingUp} label="Points restants" value={data.remaining_points.toLocaleString("fr-FR")} sub="pour les prochaines briques" color="text-amber-600 bg-amber-50" testid="remaining-points" />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        {/* Monthly chart */}
        <section className="glass-card p-5 lg:col-span-2" data-testid="monthly-chart">
          <h2 className="font-bold text-lg mb-4">Bilan mensuel des investissements</h2>
          {data.monthly_summary.length === 0 ? (
            <div className="text-sm text-[var(--ink-soft)] py-8 text-center">Aucun investissement enregistre. Ajoutez votre premier achat de credits.</div>
          ) : (
            <div className="space-y-3">
              {data.monthly_summary.map((m) => (
                <div key={m.month} className="flex items-center gap-3" data-testid={`month-row-${m.month}`}>
                  <div className="w-20 text-sm font-semibold">{monthLabel(m.month)}</div>
                  <div className="flex-1 h-5 bg-orange-50 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full transition-all"
                      style={{ width: `${Math.round((m.total / maxMonth) * 100)}%` }}
                    />
                  </div>
                  <div className="w-32 text-right text-sm font-bold text-orange-600">{fcfa(m.total)}</div>
                  <div className="w-20 text-right text-xs text-[var(--ink-soft)] hidden sm:block">{m.credits} cred.</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Phases progression */}
        <section className="glass-card p-5" data-testid="phases-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-lg">Progression des briques</h2>
            <button onClick={() => setShowPhase(true)} className="text-orange-600 hover:text-orange-700" data-testid="add-phase-btn" title="Ajouter une phase">
              <Plus size={18} />
            </button>
          </div>
          <div className="h-3 bg-emerald-50 rounded-full overflow-hidden mb-2">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full transition-all" style={{ width: `${phasesPct}%` }} />
          </div>
          <div className="text-xs text-[var(--ink-soft)] mb-4">{data.phases_completed}/{data.phases.length} phases terminees ({phasesPct}%)</div>
          {data.phases.length === 0 ? (
            <div className="text-sm text-[var(--ink-soft)] py-4 text-center">Aucune phase. Ajoutez les etapes du projet.</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {data.phases.map((p) => (
                <div key={p.id} className="flex items-center gap-2 group" data-testid={`phase-${p.id}`}>
                  <button onClick={() => togglePhase(p)} className={p.completed ? "text-emerald-500" : "text-gray-300 hover:text-emerald-400"} data-testid={`toggle-phase-${p.id}`}>
                    {p.completed ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold truncate ${p.completed ? "line-through text-[var(--ink-soft)]" : ""}`}>{p.phase_name}</div>
                    {p.completion_date && <div className="text-[10px] text-emerald-600">Terminee le {p.completion_date}</div>}
                  </div>
                  {p.estimated_points > 0 && <span className="text-xs font-bold text-amber-600">{p.estimated_points} pts</span>}
                  <button onClick={() => removePhase(p.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity" data-testid={`delete-phase-${p.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Investments table */}
      <section className="glass-card p-5 mt-6" data-testid="investments-table-section">
        <h2 className="font-bold text-lg mb-4">Historique des investissements ({data.investments.length})</h2>
        {data.investments.length === 0 ? (
          <div className="text-sm text-[var(--ink-soft)] py-8 text-center">Aucun investissement enregistre.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--ink-soft)] border-b border-orange-100">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Montant</th>
                  <th className="py-2 pr-4">Credits</th>
                  <th className="py-2 pr-4">Description</th>
                  <th className="py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.investments.map((inv) => (
                  <tr key={inv.id} className="border-b border-orange-50 hover:bg-orange-50/40" data-testid={`investment-row-${inv.id}`}>
                    <td className="py-3 pr-4 font-semibold whitespace-nowrap">{inv.date}</td>
                    <td className="py-3 pr-4 font-bold text-orange-600 whitespace-nowrap">{fcfa(inv.amount)}</td>
                    <td className="py-3 pr-4">{inv.credits > 0 ? inv.credits.toLocaleString("fr-FR") : "—"}</td>
                    <td className="py-3 pr-4 text-[var(--ink-soft)]">{inv.description || "—"}</td>
                    <td className="py-3 text-right">
                      <button onClick={() => removeInvestment(inv.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-investment-${inv.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Add investment modal */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-investment-modal">
          <DialogHeader><DialogTitle>Ajouter un investissement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-semibold">Montant (FCFA) *</label>
              <Input type="number" min="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Ex: 15000" data-testid="input-amount" />
            </div>
            <div>
              <label className="text-sm font-semibold">Credits Emergent achetes</label>
              <Input type="number" min="0" value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} placeholder="Ex: 25" data-testid="input-credits" />
            </div>
            <div>
              <label className="text-sm font-semibold">Date *</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="input-date" />
            </div>
            <div>
              <label className="text-sm font-semibold">Description</label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex: Achat de credits pour le module chat" data-testid="input-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Annuler</Button>
            <Button onClick={saveInvestment} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="save-investment-btn">
              {saving ? <Loader2 size={16} className="animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add phase modal */}
      <Dialog open={showPhase} onOpenChange={setShowPhase}>
        <DialogContent data-testid="add-phase-modal">
          <DialogHeader><DialogTitle>Ajouter une phase du projet</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-semibold">Nom de la phase *</label>
              <Input value={phaseForm.phase_name} onChange={(e) => setPhaseForm({ ...phaseForm, phase_name: e.target.value })} placeholder="Ex: Developpement du chat" data-testid="input-phase-name" />
            </div>
            <div>
              <label className="text-sm font-semibold">Points estimes (credits)</label>
              <Input type="number" min="0" value={phaseForm.estimated_points} onChange={(e) => setPhaseForm({ ...phaseForm, estimated_points: e.target.value })} placeholder="Ex: 20" data-testid="input-phase-points" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPhase(false)}>Annuler</Button>
            <Button onClick={savePhase} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="save-phase-btn">
              {saving ? <Loader2 size={16} className="animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <div className="glass-card p-4 relative overflow-hidden" data-testid={testid}>
      <div className={`absolute -right-4 -top-4 w-20 h-20 rounded-full ${color.split(" ")[1]} opacity-50`} aria-hidden />
      <div className={`relative inline-flex items-center gap-2 ${color.split(" ")[0]}`}>
        <Icon size={16} />
        <span className="text-[10px] uppercase tracking-widest font-bold">{label}</span>
      </div>
      <div className="text-2xl font-extrabold mt-2 relative">{value}</div>
      {sub && <div className="text-xs text-[var(--ink-soft)] mt-0.5 relative">{sub}</div>}
    </div>
  );
}
